import { useNavigate } from '@tanstack/react-router'
import { useRuntimeVariables } from 'app-kit'
import { AssetViz } from 'features/assetViz'
import {
  DEFAULT_SCENE_CONFIG,
  useSlashCommandAssetVizData,
} from 'features/richText/hooks/useSlashCommandAssetVizData'
import { useRolodexEntries } from 'features/rolodex'
import React, { useCallback, useMemo, useState } from 'react'
import { getSortedAssetModels, RolodexEntryType } from 'types'

import { SlashCommandCardProps } from './DefaultSlashCommandCard'
import {
  AssetVizContainer,
  ClickableSlashCommandCardName,
  SlashCommandCard,
  SlashCommandCardBody,
  SlashCommandCardHeader,
  SlashCommandCardIcon,
  SlashCommandCardInfo,
  SlashCommandCardMeta,
  SlashCommandCardType,
  SlashCommandChip,
  SlashCommandChipIcon,
} from './sharedStyles'
import { UnitConfigurationModal } from './UnitConfigurationModal'

export const UnitSlashCommandCard: React.FC<SlashCommandCardProps> = ({
  entryType,
  entryID,
  entryName,
  isEditMode,
  onClick,
  metadata,
  onMetadataChange,
}) => {
  const [showModal, setShowModal] = useState(false)
  const navigate = useNavigate()
  const { BASE_PATH } = useRuntimeVariables()

  const rolodexOptions = useMemo(
    () =>
      entryID
        ? {
            type: [
              RolodexEntryType.UNIT,
              RolodexEntryType.ASSET_MODEL,
              RolodexEntryType.COMPONENT,
              RolodexEntryType.COMPONENT_DATA_FRAME,
              RolodexEntryType.COMPONENT_DATASET,
              RolodexEntryType.COMPONENT_DEFAULTS,
            ],
            tags: { unit: entryID },
          }
        : undefined,
    [entryID]
  )

  const {
    units,
    assetModels,
    components,
    componentDataFrames,
    componentDefaults,
    componentDatasets,
  } = useRolodexEntries(rolodexOptions)

  const unit = useMemo(() => units.at(0), [units])
  const assetModel = useMemo(
    () => getSortedAssetModels([...assetModels])[0],
    [assetModels]
  )

  const {
    latestComponentDataFrames,
    displayedComponentDataFrames,
    getColorThresholdForComponent,
    getFilteredValueRange,
  } = useSlashCommandAssetVizData({
    componentDataFrames,
    componentDatasets,
    componentDefaults,
    metadata,
  })

  const isMCVConfigured = useMemo(() => {
    return Array.from(assetModel?.data.components?.values() ?? []).some(
      (component) => component.sceneConfig
    )
  }, [assetModel])

  const handleClick = useCallback(
    (event?: React.MouseEvent) => {
      if (isEditMode) {
        setShowModal(true)
        onClick?.()
      } else if (unit) {
        const siteId = unit.tags.site

        if (!siteId) {
          console.warn('No site ID found for unit:', entryID)
          return
        }

        if (event?.metaKey || event?.ctrlKey) {
          const baseUrl = BASE_PATH ? `/${BASE_PATH}` : ''
          const url = `${baseUrl}/assets/${siteId}/${entryID}`
          window.open(url, '_blank')
          return
        }

        navigate({
          to: '/assets/$siteID/$unitID',
          params: {
            siteID: siteId,
            unitID: entryID,
          },
          search: {
            view: 'details',
          },
        })
      }
    },
    [unit, entryID, isEditMode, onClick, navigate, BASE_PATH]
  )

  const handleCloseModal = () => setShowModal(false)

  if (isEditMode) {
    return (
      <>
        <SlashCommandChip
          onClick={handleClick}
          title={`${entryType}: ${entryName}`}>
          <SlashCommandChipIcon>U</SlashCommandChipIcon>
          {entryName}
        </SlashCommandChip>
        {showModal && (
          <UnitConfigurationModal
            entryName={entryName}
            assetModel={assetModel}
            components={[...components]}
            componentDataFrames={latestComponentDataFrames}
            allComponentDataFrames={[...componentDataFrames]}
            componentDefaults={[...componentDefaults]}
            metadata={metadata}
            onMetadataChange={onMetadataChange}
            onClose={handleCloseModal}
          />
        )}
      </>
    )
  }

  // In view mode, show rich display
  return (
    <SlashCommandCard $isEditMode={false} $disableHovering>
      <SlashCommandCardHeader>
        <SlashCommandCardIcon>U</SlashCommandCardIcon>
        <SlashCommandCardInfo>
          <ClickableSlashCommandCardName onClick={handleClick}>
            {entryName}
          </ClickableSlashCommandCardName>
          <SlashCommandCardType>Unit</SlashCommandCardType>
        </SlashCommandCardInfo>
      </SlashCommandCardHeader>
      <SlashCommandCardBody $isEditMode={false}>
        <SlashCommandCardMeta>
          <span>ID: {entryID}</span>
          {unit?.data.name && <span>Name: {unit?.data.name}</span>}
          {unit?.data.area && <span>Area: {unit?.data.area}</span>}
        </SlashCommandCardMeta>

        {isMCVConfigured &&
          assetModel &&
          displayedComponentDataFrames.length > 0 && (
            <AssetVizContainer>
              <AssetViz
                assetModel={assetModel}
                components={components}
                componentDataFrames={displayedComponentDataFrames}
                showMultiComponentView
                markupLayers={[]}
                getColorThresholdForComponent={getColorThresholdForComponent}
                cdfToFilteredValueRange={getFilteredValueRange}
                sceneConfig={DEFAULT_SCENE_CONFIG}
              />
            </AssetVizContainer>
          )}
      </SlashCommandCardBody>
    </SlashCommandCard>
  )
}
